/**
 * CSS filter stack for a page block. Missing keys mean the identity value,
 * which is left out of the generated `filter` string.
 */

const SCALAR = [
    { key: 'blur', label: 'Blur', min: 0, max: 40, step: 1, unit: 'px', identity: 0, css: (n) => `blur(${n}px)` },
    { key: 'brightness', label: 'Brightness', min: 0, max: 200, step: 1, unit: '%', identity: 100, css: (n) => `brightness(${n}%)` },
    { key: 'contrast', label: 'Contrast', min: 0, max: 200, step: 1, unit: '%', identity: 100, css: (n) => `contrast(${n}%)` },
    { key: 'hueRotate', label: 'Hue rotate', min: 0, max: 360, step: 1, unit: 'deg', identity: 0, css: (n) => `hue-rotate(${n}deg)` },
    { key: 'grayscale', label: 'Grayscale', min: 0, max: 100, step: 1, unit: '%', identity: 0, css: (n) => `grayscale(${n}%)` },
    { key: 'invert', label: 'Invert', min: 0, max: 100, step: 1, unit: '%', identity: 0, css: (n) => `invert(${n}%)` },
    { key: 'opacity', label: 'Opacity', min: 0, max: 100, step: 1, unit: '%', identity: 100, css: (n) => `opacity(${n}%)` },
    { key: 'saturate', label: 'Saturate', min: 0, max: 200, step: 1, unit: '%', identity: 100, css: (n) => `saturate(${n}%)` },
    { key: 'sepia', label: 'Sepia', min: 0, max: 100, step: 1, unit: '%', identity: 0, css: (n) => `sepia(${n}%)` },
];

export const FILTER_FIELDS = SCALAR;

export const DROP_SHADOW = {
    x: { min: -40, max: 40, step: 1 },
    y: { min: -40, max: 40, step: 1 },
    blur: { min: 0, max: 40, step: 1 },
};

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function shadowColor(value) {
    if (typeof value !== 'string') return '#000000';
    const text = value.trim();
    return /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(text) ? text : '#000000';
}

/** Non-identity filter fields, or null when the block is unfiltered. */
export function normalizeFilter(value) {
    if (!value || typeof value !== 'object') return null;
    const out = {};
    SCALAR.forEach((field) => {
        const n = Number(value[field.key]);
        if (!Number.isFinite(n)) return;
        const next = clamp(n, field.min, field.max);
        if (next !== field.identity) out[field.key] = next;
    });
    const behind = Number(value.backdropInvert);
    if (Number.isFinite(behind)) {
        const next = clamp(behind, 0, 100);
        if (next !== 0) out.backdropInvert = next;
    }
    const shadow = value.dropShadow;
    if (shadow && typeof shadow === 'object') {
        const x = clamp(Number(shadow.x) || 0, DROP_SHADOW.x.min, DROP_SHADOW.x.max);
        const y = clamp(Number(shadow.y) || 0, DROP_SHADOW.y.min, DROP_SHADOW.y.max);
        const blur = clamp(Number(shadow.blur) || 0, DROP_SHADOW.blur.min, DROP_SHADOW.blur.max);
        const color = shadowColor(shadow.color);
        if (x || y || blur || color !== '#000000') {
            out.dropShadow = { x, y, blur, color };
        }
    }
    return Object.keys(out).length ? out : null;
}

/** The value a slider should show, including identity defaults. */
export function filterValue(value, key) {
    if (key === 'backdropInvert') {
        const n = Number(value?.backdropInvert);
        return Number.isFinite(n) ? clamp(n, 0, 100) : 0;
    }
    const field = SCALAR.find((entry) => entry.key === key);
    if (!field) return 0;
    const n = Number(value?.[key]);
    return Number.isFinite(n) ? clamp(n, field.min, field.max) : field.identity;
}

export function dropShadowValue(value) {
    const shadow = value?.dropShadow;
    return {
        x: clamp(Number(shadow?.x) || 0, DROP_SHADOW.x.min, DROP_SHADOW.x.max),
        y: clamp(Number(shadow?.y) || 0, DROP_SHADOW.y.min, DROP_SHADOW.y.max),
        blur: clamp(Number(shadow?.blur) || 0, DROP_SHADOW.blur.min, DROP_SHADOW.blur.max),
        color: shadowColor(shadow?.color),
    };
}

const FILTER_ORDER = ['blur', 'brightness', 'contrast', 'hueRotate', 'grayscale', 'invert', 'opacity', 'saturate', 'sepia'];

function orderedParts(filter) {
    const parts = [];
    const names = [];
    FILTER_ORDER.forEach((key) => {
        const field = SCALAR.find((entry) => entry.key === key);
        if (key === 'hueRotate' && filter.dropShadow && (filter.dropShadow.x || filter.dropShadow.y || filter.dropShadow.blur)) {
            const shadow = filter.dropShadow;
            parts.push(`drop-shadow(${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color})`);
            names.push('Drop shadow');
        }
        if (filter[key] == null) return;
        parts.push(field.css(filter[key]));
        names.push(field.label);
    });
    return { parts, names };
}

/** CSS `filter` value, or undefined when nothing would change the block's own pixels. */
export function cssFilter(value) {
    const filter = normalizeFilter(value);
    if (!filter) return undefined;
    const { parts } = orderedParts(filter);
    return parts.join(' ') || undefined;
}

/** Inverts the pixels behind the block. Identity is undefined. */
export function cssBackdropFilter(value) {
    const amount = normalizeFilter(value)?.backdropInvert;
    if (!amount) return undefined;
    return `invert(${amount}%)`;
}

export function hasActiveFilter(value) {
    return Boolean(cssFilter(value) || cssBackdropFilter(value));
}

export function filterSummary(value) {
    const filter = normalizeFilter(value);
    if (!filter) return 'None';
    const names = orderedParts(filter).names;
    if (filter.backdropInvert) names.push('Invert behind');
    return names.join(', ') || 'None';
}

export function filterDescription(value) {
    const parts = [cssFilter(value), cssBackdropFilter(value) && `behind ${cssBackdropFilter(value)}`].filter(Boolean);
    return parts.join(' · ') || 'None';
}
